import type { CognitiveScarcityBudget, StateProbe, Fragment, AmbiguityHold } from '../types'

/**
 * Pure, deterministic Cognitive Scarcity Budget computation.
 * This is the authoritative core. AI is used only for optional polishing / embedding.
 * Never interprets the client's life or tells the care team what "really" happened.
 */
export function computeBudget(
  fragments: Fragment[],
  probes: StateProbe[],
  holds: AmbiguityHold[],
  caseLabel: string
): CognitiveScarcityBudget {
  const now = Date.now()

  const fragmentsCount = fragments.length
  const charitableCount = fragments.reduce((sum, f) => 
    sum + f.interpretations.filter(i => i.lens === 'charitable' || i.lens === 'structural').length, 0)

  const holdsCount = holds.length

  // Trajectory
  const agencies = probes.map(p => p.agency)
  const valences = probes.map(p => p.valence)
  const avgAgency = agencies.length ? Math.round(agencies.reduce((a, b) => a + b, 0) / agencies.length) : 55
  const avgValence = valences.length ? (valences.reduce((a, b) => a + b, 0) / valences.length) : -0.5

  const attentionMix: Record<string, number> = {}
  probes.forEach(p => {
    attentionMix[p.attentionState] = (attentionMix[p.attentionState] || 0) + 1
  })
  const totalAttn = Object.values(attentionMix).reduce((a, b) => a + b, 0) || 1
  Object.keys(attentionMix).forEach(k => { attentionMix[k] = Math.round((attentionMix[k] / totalAttn) * 100) })

  // Scarcity estimate: higher when low agency + negative valence + many fragments without charitable re-readings + few holds (forcing resolution costs capacity)
  const baseLoad = Math.min(100, Math.max(12, fragmentsCount * 4.2 + (100 - avgAgency) * 0.55 + Math.max(0, -avgValence) * 7 ))
  const resolutionPressure = Math.max(0, (fragmentsCount - charitableCount * 0.6) * 3.5)
  const ambiguityCredit = Math.min(22, holdsCount * 4.8) // holding ambiguity is a repair act that preserves capacity
  const scarcityEstimate = Math.round(Math.max(18, Math.min(96, baseLoad + resolutionPressure - ambiguityCredit)))

  // Allocation (sums ~100). Reflects where attention actually went in the session.
  const dissection = Math.min(38, Math.max(18, Math.round(fragmentsCount * 3.8 + probes.length * 1.6)))
  const charitableRe = Math.min(34, Math.max(12, Math.round(charitableCount * 2.8 + holdsCount * 0.6)))
  const holding = Math.min(28, Math.max(6, Math.round(holdsCount * 5.5 + (100 - avgAgency) * 0.12)))
  const awareness = Math.min(18, Math.max(6, Math.round(probes.length * 2.1)))
  const synthesis = Math.max(6, 100 - dissection - charitableRe - holding - awareness)

  const allocation = {
    dissection: Math.round(dissection),
    charitableRe: Math.round(charitableRe),
    holding: Math.round(holding),
    awareness: Math.round(awareness),
    synthesis: Math.round(synthesis),
  }

  // Tolerance score: celebrates deliberate non-resolution + charitable labor
  const toleranceScore = Math.min(94, Math.max(18, 
    Math.round( (holdsCount * 11) + (charitableCount * 3.5) + (avgAgency > 45 ? 9 : 0) - (fragmentsCount > 9 ? 7 : 0) )
  ))

  // Concrete repair actions — generated from the actual labor performed + last chosen next actions
  const lastActions = probes.slice(-3).map(p => p.nextAction).filter(Boolean)
  const repairActions: string[] = []

  if (charitableCount >= 2) {
    repairActions.push("Send one charitable re-reading sentence to the caseworker or supervisor that names a structural barrier without blame.")
  }
  if (holdsCount >= 1) {
    repairActions.push("Name one ambiguity explicitly in the next team note: 'We are choosing to hold X for now rather than force a resolution that may harm trust.'")
  }
  if (avgAgency < 45) {
    repairActions.push("Schedule a 12-minute team huddle this week with the single question: 'What would increase our real degrees of freedom on this case by 10%?'")
  }
  if (lastActions.length) {
    repairActions.push(`Honor the last logged next action: "${lastActions[lastActions.length-1].slice(0, 110)}" — make it the first 15-minute task on the next shift.`)
  }
  if (repairActions.length < 3) {
    repairActions.push("Create a one-sentence 'client dignity anchor' that any team member can read aloud before touching this file again.")
  }
  while (repairActions.length < 3) {
    repairActions.push("Log one more state probe after the next real-world contact and compare agency/valence movement.")
  }

  // Human readable narrative
  const fullText = [
    `Cognitive Scarcity Budget — ${caseLabel}`,
    ``,
    `This bench session treated interpretive labor as measurable craft rather than invisible tax.`,
    `Fragments examined: ${fragmentsCount}. Charitable / structural re-readings offered: ${charitableCount}.`,
    `Ambiguity holds logged: ${holdsCount} (each hold is an act of capacity preservation).`,
    ``,
    `Attention trajectory: avg agency ${avgAgency}%, avg valence ${avgValence.toFixed(1)}.`,
    `Dominant attention states: ${Object.entries(attentionMix).sort((a,b)=>b[1]-a[1]).slice(0,2).map(([k,v])=>`${k} ${v}%`).join(', ')}.`,
    ``,
    `Estimated cognitive scarcity load for this case right now: ~${scarcityEstimate} units (lower is not always better; the goal is honest mapping).`,
    `Tolerance / ambiguity credit: ${toleranceScore}.`,
    ``,
    `Repair actions synthesized from the actual work done on this bench:`,
    ...repairActions.map((a,i)=>`${i+1}. ${a}`),
    ``,
    `This budget belongs to the team that made it. It is not a score. It is a snapshot for later re-weighing.`
  ].join('\n')

  return {
    castAt: now,
    scarcityEstimate,
    allocation,
    toleranceScore,
    repairActions: repairActions.slice(0, 4),
    fullText,
    probeTrajectory: {
      avgAgency,
      avgValence: Math.round(avgValence * 10) / 10,
      attentionMix,
    },
    holdsCount,
    fragmentsCount,
    charitableCount,
  }
}

export function formatValence(v: number): string {
  if (v <= -3) return 'heavy / drained'
  if (v <= -1) return 'tense'
  if (v === 0) return 'steady'
  if (v <= 2) return 'clearing'
  return 'light / possible'
}

export function formatAgency(a: number): string {
  if (a < 25) return 'very low'
  if (a < 45) return 'constrained'
  if (a < 65) return 'partial'
  if (a < 82) return ' workable'
  return 'high'
}
